import React, { useMemo } from "react";
import { Box, Chip, Typography } from "@mui/material";
import axios from "axios";
import { useQuery } from "@tanstack/react-query";

// возьми тот же util, что и в MarketHeaderMetrics / AIAvatars
import { fetchSelectedTokensWithCache } from "../components/StreamSignal/utils";

const GLASS = {
  bg: "linear-gradient(135deg, rgba(18,22,28,.78) 0%, rgba(10,13,17,.78) 100%)",
  border: "1px solid rgba(255,255,255,.08)",
  text: "#e5e7eb",
  subtext: "#9ca3af",
};

const pillSx = {
  height: 24,
  borderRadius: 999,
  fontWeight: 900,
  border: "1px solid rgba(255,255,255,.12)",
  color: "#e5e7eb",
  background: "linear-gradient(135deg, rgba(18,22,28,.88) 0%, rgba(10,13,17,.88) 100%)",
  boxShadow: "0 10px 26px rgba(2,8,23,.32)",
  "& .MuiChip-label": { px: 1.1, letterSpacing: ".01em" },
};

const fmtT = (v) => (Number.isFinite(Number(v)) ? (Number(v) / 1e12).toFixed(2) + "T" : "—");
const fmtB = (v) => (Number.isFinite(Number(v)) ? (Number(v) / 1e9).toFixed(1) + "B" : "—");

const fgiLabel = (v) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return "N/A";
  if (n <= 25) return "Extreme Fear";
  if (n <= 50) return "Fear";
  if (n <= 75) return "Greed";
  return "Extreme Greed";
};

export default function MarketTickerMarquee() {
  const { data: tokenData = [] } = useQuery({
    queryKey: ["ticker_tokens_selected"],
    queryFn: fetchSelectedTokensWithCache,
    staleTime: 0,
    refetchInterval: 30000,
    refetchOnWindowFocus: "always",
    refetchOnReconnect: "always",
    retry: 2,
  });

  const { data: globalData } = useQuery({
    queryKey: ["ticker_globalData"],
    queryFn: async () => {
      const { data } = await axios.get(
        "https://europe-west3-starwa-94ac5.cloudfunctions.net/getCoinGeckoGlobal"
      );
      return data;
    },
    staleTime: 15 * 60 * 1000,
    retry: 2,
  });

  const { data: fngValue } = useQuery({
    queryKey: ["ticker_fearGreedIndex"],
    queryFn: async () => {
      const { data } = await axios.get("https://api.alternative.me/fng/");
      return data?.data?.[0]?.value;
    },
    staleTime: 15 * 60 * 1000,
    retry: 2,
  });

  const marketData = useMemo(() => {
    if (!globalData) return null;
    return {
      marketCap: globalData.total_market_cap?.usd,
      volume24h: globalData.total_volume?.usd,
      btcDominance: globalData.market_cap_percentage?.btc,
      ethDominance: globalData.market_cap_percentage?.eth,
    };
  }, [globalData]);

  const buyWaitSignal = useMemo(() => {
    const safe = { status: "WAIT", confidence: 50, buy: 50 };
    if (!Array.isArray(tokenData) || tokenData.length === 0 || !marketData?.marketCap) return safe;

    const changes = tokenData.map((c) => Number(c.usd_24h_change)).filter(Number.isFinite);
    const n = changes.length;
    if (!n) return safe;

    const avg = changes.reduce((a, b) => a + b, 0) / n;
    const sdRaw = Math.sqrt(changes.reduce((s, v) => s + (v - avg) ** 2, 0) / n);
    const sd = sdRaw || 1;

    const breadth = changes.filter((v) => v > 0).length / n;
    const volMcap =
      marketData?.volume24h && marketData?.marketCap ? marketData.volume24h / marketData.marketCap : null;

    const fgi = Number(fngValue);
    const trendScore = Math.max(-2, Math.min(2, avg / sd)) / 2;
    const breadthScore = (breadth - 0.5) * 2;
    const fearScore = Number.isFinite(fgi) ? (fgi - 50) / 50 : 0;

    const baseTurnover = 0.06;
    const turnoverScore =
      volMcap == null ? 0 : Math.max(-1, Math.min(1, (volMcap - baseTurnover) / baseTurnover));

    const score = 0.45 * trendScore + 0.25 * breadthScore + 0.15 * fearScore + 0.15 * turnoverScore;

    const buy = Math.round(((score + 1) / 2) * 100);
    const confidence = Math.round(Math.min(1, Math.abs(score)) * 100);
    const status = score >= 0.35 ? "BUY" : "WAIT";

    return { status, confidence, buy };
  }, [tokenData, marketData, fngValue]);

  const pills = useMemo(() => {
    const items = [];

    items.push({
      key: "signal",
      label: `${buyWaitSignal.status} • ${buyWaitSignal.confidence}%`,
      tone: buyWaitSignal.status === "BUY" ? "buy" : "wait",
    });

    items.push({
      key: "dom",
      label: `BTC ${marketData?.btcDominance?.toFixed?.(1) ?? "—"}% • ETH ${
        marketData?.ethDominance?.toFixed?.(1) ?? "—"
      }%`,
    });

    items.push({
      key: "fng",
      label: `FG ${Number.isFinite(Number(fngValue)) ? fngValue : "—"} • ${fgiLabel(fngValue)}`,
    });

    items.push({
      key: "capvol",
      label: `MCap ${fmtT(marketData?.marketCap)} • Vol ${fmtB(marketData?.volume24h)}`,
    });

    // топ-6 выбранных токенов
    const top = (Array.isArray(tokenData) ? tokenData : [])
      .slice(0, 6)
      .map((t, i) => {
        const sym = (t?.symbol || t?.name || `T${i}`).toUpperCase();
        const ch = Number(t?.usd_24h_change);
        const sign = Number.isFinite(ch) ? (ch >= 0 ? "+" : "") : "";
        const val = Number.isFinite(ch) ? `${sign}${ch.toFixed(2)}%` : "—";
        return { key: `t-${sym}-${i}`, label: `${sym} ${val}`, tone: Number.isFinite(ch) ? (ch >= 0 ? "up" : "down") : "neutral" };
      });

    items.push(...top);
    return items;
  }, [buyWaitSignal, marketData, fngValue, tokenData]);

  const chipSx = (tone) => {
    if (tone === "buy") return { ...pillSx, border: "1px solid rgba(34,197,94,.40)", boxShadow: "0 0 36px rgba(34,197,94,.10)" };
    if (tone === "wait") return { ...pillSx, border: "1px solid rgba(244,63,94,.40)", boxShadow: "0 0 36px rgba(244,63,94,.10)" };
    if (tone === "up") return { ...pillSx, border: "1px solid rgba(34,197,94,.35)", color: "#bbf7d0" };
    if (tone === "down") return { ...pillSx, border: "1px solid rgba(244,63,94,.35)", color: "#fecdd3" };
    return pillSx;
  };

  // для “бесшовной” прокрутки дублируем контент
  const loop = [...pills, ...pills];

  return (
  <Box
    sx={{
      width: "100%",
      overflow: "hidden",
      borderRadius: 14,
      border: "1px solid rgba(255,255,255,.08)",
      background: "linear-gradient(135deg, rgba(18,22,28,.88) 0%, rgba(10,13,17,.88) 100%)",
      boxShadow: "0 10px 32px rgba(2,8,23,.45)",
      backdropFilter: "blur(10px)",
    }}
  >
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, px: 1.25, py: 0.9 }}>
      <Typography
  sx={{
    flexShrink: 0,
    fontSize: 10,
    fontWeight: 950,
    letterSpacing: ".22em",
    textTransform: "uppercase",
    color: "#93c5fd",
    opacity: 0.95,

    // ✅ нужный шрифт
    fontFamily: "'Bruno Ace SC', system-ui, -apple-system, Segoe UI, Roboto, Arial, sans-serif",
    textRendering: "geometricPrecision",
  }}
>
  PromosPay
</Typography>

      <Box
        sx={{
          position: "relative",
          flex: 1,
          minWidth: 0,
          overflow: "hidden",
          // fade по краям — выглядит сильно богаче
          maskImage:
            "linear-gradient(to right, transparent 0%, black 10%, black 90%, transparent 100%)",
          WebkitMaskImage:
            "linear-gradient(to right, transparent 0%, black 10%, black 90%, transparent 100%)",
        }}
      >
        <Box
          className="pp-ticker-track"
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 1,
            whiteSpace: "nowrap",
            willChange: "transform",
            animation: "ppTicker 32s linear infinite",
            "@keyframes ppTicker": {
              "0%": { transform: "translateX(0)" },
              "100%": { transform: "translateX(-50%)" },
            },
            // пауза при hover
            "&:hover": { animationPlayState: "paused" },
          }}
        >
          {loop.map((it, idx) => (
            <Chip
              key={`${it.key}-${idx}`}
              label={it.label}
              sx={{
                ...chipSx(it.tone),
                height: 24,
                borderRadius: 999,
                background:
                  "linear-gradient(135deg, rgba(18,22,28,.92) 0%, rgba(10,13,17,.92) 100%)",
                border: "1px solid rgba(255,255,255,.14)",
                boxShadow: "0 10px 26px rgba(2,8,23,.30)",
              }}
            />
          ))}
        </Box>
      </Box>
    </Box>
  </Box>
);

}
